import { MAP_ENABLED } from '../../config/features'

export type TourCtx = { discogsConnected: boolean }

export type ChapterPhase = 'foundation' | 'discovery'

export interface TourStep {
  id: string
  /** Route to navigate to when this step starts. */
  path?: string
  /** CSS selector for the spotlight target. Omit for floating steps. */
  target?: string
  title: string
  body: string
  placement?: 'top' | 'bottom' | 'left' | 'right'
  /**
   * Watch for a DOM event on the target. Soft auto-advance — never blocks.
   */
  observe?: {
    event: 'click' | 'change' | 'input'
    count?: number
    hint?: string
  }
  /**
   * Mark this step as a critical acknowledgement. Triggers stronger visual
   * treatment (warning tone, "Heads up" eyebrow) and forces a 4-second
   * minimum read pace before the CTA enables. The CTA reads
   * "I understand — continue" instead of "Got it."
   */
  forceAck?: boolean
}

export interface TourChapter {
  id: string
  phase: ChapterPhase
  /** Display order within phase. */
  order: number
  title: string
  /** Short index-row summary. */
  summary: string
  icon: string
  steps: TourStep[]
  /** Hide this chapter entirely if false. */
  skipIf?: (ctx: TourCtx) => boolean
  /**
   * For foundation chapters: the explicit "stage done" beat shown between
   * chapters. The user must explicitly start the next chapter — no auto-chain.
   */
  completionBeat?: {
    title: string
    body: string
  }
}

export const CHAPTERS: TourChapter[] = [

  // ═══════════════════════════════════════════════════════════════════
  //  PHASE 1 — FOUNDATION (mandatory, ordered)
  // ═══════════════════════════════════════════════════════════════════

  {
    id: 'welcome',
    phase: 'foundation',
    order: 1,
    title: 'Welcome',
    summary: 'A 60-second intro to what tenwords is and how it thinks.',
    icon: 'bi-stars',
    steps: [
      {
        id: 'welcome-intro',
        title: 'Welcome to tenwords',
        body: 'tenwords is a discovery tool for vinyl, built around your taste. Across the next five chapters we\'ll walk through how to browse the catalogue, why artists and labels matter, and how to connect your Discogs account so the app can work with your real collection.',
      },
      {
        id: 'welcome-pace',
        title: 'Set your own pace',
        body: 'The tour is broken into short chapters. Each step gives you time to read before the button enables, and the catalogue underneath stays fully interactive — explore, click around, then come back. The tour pill in the top-right always remembers where you were.',
      },
      {
        id: 'welcome-mandatory',
        title: 'Five short chapters to start',
        body: 'These first chapters are the foundation — Welcome, Releases, Artists, Labels, then connecting Discogs and syncing. They unlock the rest of the app and only take a few minutes. After that, more chapters open up for things you can dive into on your own time.',
      },
    ],
    completionBeat: {
      title: 'Nicely done.',
      body: 'You\'re set up to take the tour. Next chapter: Releases — the main canvas of the app.',
    },
  },

  // ─────────────────────────────────────────────────────────────
  {
    id: 'releases',
    phase: 'foundation',
    order: 2,
    title: 'Releases',
    summary: 'The main canvas — browsing, filtering, sorting the catalogue.',
    icon: 'bi-collection',
    steps: [
      {
        id: 'releases-overview',
        path: '/releases',
        title: 'This is the catalogue',
        body: 'Every release tenwords knows about lives here. Each card shows the cover, artist, title, and key signals — rating, want/have ratio, median price, and Gem Score: our demand-validated ranking. Take a moment to scan the page before continuing.',
      },
      {
        id: 'releases-filter-toggle',
        path: '/releases',
        target: '[data-tour="filter-toggle"]',
        placement: 'bottom',
        title: 'Open the filter panel',
        body: 'Narrow the catalogue by genre, style, year, price or demand. Same panel lives on every list page in the app — learn it here, you\'ll know it everywhere.',
        observe: { event: 'click', hint: 'Click Filters to open the panel' },
      },
      {
        id: 'releases-filter-panel',
        path: '/releases',
        target: '[data-tour="filter-panel"]',
        placement: 'bottom',
        title: 'What\'s in the panel',
        body: 'Genres on the left, styles on the right, year range and price below. Pick whatever interests you — selections accumulate, and the panel stays open while you browse.',
      },
      {
        id: 'releases-filter-mode',
        path: '/releases',
        target: '[data-tour="style-mode-toggle"]',
        placement: 'bottom',
        title: 'OR vs AND — how styles combine',
        body: 'This toggle changes how multiple style picks combine. ⊍ OR returns releases matching any selected style — broad. ∩ AND requires all of them — narrow, fewer results. Switch when you want a specific intersection like "ambient AND dub."',
      },
      {
        id: 'releases-sort',
        path: '/releases',
        target: '[data-tour="sort-select"]',
        placement: 'bottom',
        title: 'Sort by any signal',
        body: 'Sort by Gem Score for tenwords\'s demand-validated recommendation, by Rating for crowd consensus, by Median price for value plays. Your choice sticks across sessions, on every list page.',
      },
      {
        id: 'releases-detail',
        path: '/releases',
        title: 'Click any release to dive in',
        body: 'A release page shows the full track list, audio analysis, sonic neighbours, market history, and the actions to add it to your Discogs Collection or Wantlist (we\'ll get to that). For now: clicking around is how you learn the catalogue.',
      },
    ],
    completionBeat: {
      title: 'Releases — covered.',
      body: 'Same browse / filter / sort pattern lives behind Artists and Labels. Up next: how Artists pages work.',
    },
  },

  // ─────────────────────────────────────────────────────────────
  {
    id: 'artists',
    phase: 'foundation',
    order: 3,
    title: 'Artists',
    summary: 'Discography as a discovery axis.',
    icon: 'bi-person-badge',
    steps: [
      {
        id: 'artists-overview',
        path: '/artists',
        title: 'Artists, aggregated',
        body: 'The Artists page lists every artist with releases in the catalogue, ordered by signals like discography size and average rating. Use it to anchor exploration on a person whose work you already trust.',
      },
      {
        id: 'artists-filter-sort',
        path: '/artists',
        target: '[data-tour="view-sort"]',
        placement: 'bottom',
        title: 'Same controls, different objects',
        body: 'Sort, filter, view modes — the same controls you learned on Releases. Try sorting by Discography Size to find prolific artists, or by Average Rating to focus on consistently-rated ones.',
      },
      {
        id: 'artists-detail',
        path: '/artists',
        title: 'Click into an artist',
        body: 'An artist page shows their full discography on tenwords, releases grouped by role (main, remix, appears on), and links to their labels. Useful when you want to see "everything Theo Parrish has done."',
      },
    ],
    completionBeat: {
      title: 'Artists — covered.',
      body: 'Labels work the same way, but they\'re a richer discovery axis than people realise. Quick one next.',
    },
  },

  // ─────────────────────────────────────────────────────────────
  {
    id: 'labels',
    phase: 'foundation',
    order: 4,
    title: 'Labels',
    summary: 'The single best vinyl discovery axis.',
    icon: 'bi-tag-fill',
    steps: [
      {
        id: 'labels-overview',
        path: '/labels',
        title: 'Why labels matter',
        body: 'A good label is a curator — they signed an artist, A&R\'d the record, pressed it. Following a label is the closest vinyl analogue to "more like this." This page lists every label with releases in the catalogue, with the same sort and filter tools you already know.',
      },
      {
        id: 'labels-detail',
        path: '/labels',
        title: 'Label pages',
        body: 'Click any label to see its catalogue ordered by release date. Look at adjacent catalogue numbers, founders, sub-labels. This is how you go from "I like this one record" to "I should hear everything around it."',
      },
    ],
    completionBeat: {
      title: 'Labels — covered.',
      body: 'You now know how to browse the catalogue from three angles. Next, the part that makes tenwords actually personal: connecting your Discogs account.',
    },
  },

  // ─────────────────────────────────────────────────────────────
  {
    id: 'discogs',
    phase: 'foundation',
    order: 5,
    title: 'Connect Discogs',
    summary: 'Link your account — this is what makes the app personal.',
    icon: 'bi-link-45deg',
    // If the user already connected (e.g. via Settings before reaching this
    // stage), skip the chapter — but the bidirectional warning below still
    // surfaces via the persistent badge on Collection/Wantlist and the
    // first-write teach modals.
    skipIf: (ctx) => ctx.discogsConnected,
    steps: [
      {
        id: 'discogs-why',
        path: '/collection',
        target: '[data-tour="discogs-connect-section"]',
        placement: 'top',
        title: 'This is where your collection will live',
        body: 'Right now the Collection page is empty because tenwords doesn\'t know what you own yet. Linking your Discogs account fills this page in and powers personalised match scores across the catalogue.' + (MAP_ENABLED ? ' It also highlights your wantlist on the audio map.' : ''),
      },
      {
        id: 'discogs-warn-bidirectional',
        path: '/collection',
        target: '[data-tour="discogs-connect-section"]',
        placement: 'top',
        title: 'Heads up: this is a two-way link',
        body: 'tenwords is a Discogs client — not a separate database. From the moment you connect, adding a release to your Collection in this app adds it to your Discogs Collection. Same for Wantlist. Removing here removes there. There is no "local-only" mode. This is intentional: one source of truth, no drift between apps.',
        forceAck: true,
      },
      {
        id: 'discogs-connect',
        path: '/collection',
        target: '[data-tour="discogs-connect-cta"]',
        placement: 'top',
        title: 'Connect when ready',
        body: 'Hit Connect Discogs to start the OAuth flow. You\'ll bounce to Discogs to authorise, then return here. The next chapter picks up once you\'re connected — your collection will be ready to sync.',
        observe: { event: 'click', hint: 'Click Connect Discogs when you\'re ready' },
      },
    ],
    completionBeat: {
      title: 'Account linked.',
      body: 'Last chapter: syncing your library, and what these pages do.',
    },
  },

  // ─────────────────────────────────────────────────────────────
  {
    id: 'sync',
    phase: 'foundation',
    order: 6,
    title: 'Sync your library',
    summary: 'Pull in your Collection and Wantlist, then meet the pages.',
    icon: 'bi-arrow-repeat',
    skipIf: (ctx) => !ctx.discogsConnected,
    steps: [
      {
        id: 'sync-trigger',
        path: '/collection',
        target: '[data-tour="discogs-sync-btn"]',
        placement: 'bottom',
        title: 'Pull your collection in',
        body: 'Hit Sync to fetch your Discogs Collection. The first run can take a couple of minutes — progress shows in the sidebar. You can keep using the app while it runs.',
        observe: { event: 'click', hint: 'Click Sync to start' },
      },
      {
        id: 'sync-collection',
        path: '/collection',
        title: 'Your Collection page',
        body: 'Once sync completes, this page shows every record you own. Sort and filter it like any other list page. Across the rest of the app, releases you own are highlighted in cyan so you always know what\'s already yours.',
      },
      {
        id: 'sync-wantlist',
        path: '/wantlist',
        title: 'Your Wantlist page',
        body: 'Same shape for your Wantlist — every record you\'ve marked you want. Wantlist entries glow lime across the rest of the app' + (MAP_ENABLED ? ', including the audio map.' : '.') + ' The Sync button on this page works the same way.',
      },
      {
        id: 'sync-reminder',
        path: '/wantlist',
        title: 'Remember the contract',
        body: 'When you click Add or Remove on a release anywhere in the app, the change goes straight to your Discogs account. The first time you do each kind of action, we\'ll show a quick confirmation so it lands — then we get out of the way.',
        forceAck: true,
      },
    ],
    completionBeat: {
      title: 'Foundation complete.',
      body: 'You\'ve got everything you need to use tenwords. The rest of the tour is optional — chapters for ' + (MAP_ENABLED ? 'the audio map, ' : '') + 'scanning sellers, shuffle and more are now available in the tour pill whenever you\'re curious.',
    },
  },

  // ═══════════════════════════════════════════════════════════════════
  //  PHASE 2 — DISCOVERY (optional, any order)
  // ═══════════════════════════════════════════════════════════════════

  {
    id: 'map',
    phase: 'discovery',
    order: 1,
    title: 'The audio map',
    summary: 'Every release plotted by sonic fingerprint.',
    icon: 'bi-map',
    skipIf: () => !MAP_ENABLED,
    steps: [
      {
        id: 'map-overview',
        path: '/map',
        target: '[data-tour="xy-map"]',
        placement: 'left',
        title: 'A two-dimensional sound space',
        body: 'Each dot is a release positioned by audio features. Similar-sounding records cluster together. Records you own are cyan; wantlist entries are lime. Drift outward from a cluster you like to find adjacent music.',
      },
    ],
  },

  {
    id: 'sellers',
    phase: 'discovery',
    order: 2,
    title: 'Scan a seller',
    summary: 'Crawl any Discogs seller and surface what matches your taste.',
    icon: 'bi-shop',
    steps: [
      {
        id: 'sellers-input',
        path: '/sellers',
        target: '[data-tour="sellers-scan"]',
        placement: 'bottom',
        title: 'Drop in a seller username',
        body: 'Paste any Discogs seller and we\'ll crawl their inventory, then highlight what fits your taste, your wantlist, or sits below the median price. Useful before placing an order.',
      },
    ],
  },

  {
    id: 'shuffle',
    phase: 'discovery',
    order: 3,
    title: 'Random discovery',
    summary: 'Roll the dice when you don\'t know what you\'re looking for.',
    icon: 'bi-shuffle',
    steps: [
      {
        id: 'shuffle-tabs',
        path: '/shuffle',
        target: '[data-tour="shuffle-tabs"]',
        placement: 'bottom',
        title: 'Three pools to roll from',
        body: 'All pulls random releases from the entire catalogue. Wantlist and Collection roll from your own lists with taste-aware weighting. Refresh to roll again. Good for when nothing specific comes to mind.',
      },
    ],
  },
]

export function visibleChapters(ctx: TourCtx): TourChapter[] {
  return CHAPTERS.filter(c => !c.skipIf?.(ctx))
}

export function foundationChapters(ctx: TourCtx): TourChapter[] {
  return visibleChapters(ctx)
    .filter(c => c.phase === 'foundation')
    .sort((a, b) => a.order - b.order)
}

export function discoveryChapters(ctx: TourCtx): TourChapter[] {
  return visibleChapters(ctx)
    .filter(c => c.phase === 'discovery')
    .sort((a, b) => a.order - b.order)
}

export function isFoundationComplete(
  ctx: TourCtx,
  completed: Record<string, boolean>
): boolean {
  const foundation = foundationChapters(ctx)
  if (foundation.length === 0) return false
  return foundation.every(c => completed[c.id])
}

export function nextFoundationChapter(
  ctx: TourCtx,
  completed: Record<string, boolean>
): TourChapter | null {
  return foundationChapters(ctx).find(c => !completed[c.id]) ?? null
}
